import "nprogress/nprogress.css";
import "~/styles/globals.scss";

import { ContentfulLivePreviewProvider } from "@contentful/live-preview/react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { logger } from "@springtree/eva-sdk-core-logger";
import { setServiceSetting } from "@springtree/eva-sdk-core-service";
import { setCoreSetting } from "@springtree/eva-sdk-core-settings";
import { Hydrate, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SpeedInsights } from "@vercel/speed-insights/next";
import type { AppProps } from "next/app";
import localFont from "next/font/local";
import Router, { useRouter } from "next/router";
import Script from "next/script";
import { AbstractIntlMessages, IntlError, IntlErrorCode, NextIntlClientProvider } from "next-intl";
import nProgress from "nprogress";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import aa from "search-insights";

import CartSync from "~/components/cart-sync";
import CountryDetectModal from "~/components/common/country-detect-modal/country-detect-modal";
import DatadogInit from "~/components/common/datadog-init";
import DidomiIntegration from "~/components/common/didomi-integration";
import GenericError from "~/components/common/generic-error";
import GetActiveShoppingCart from "~/components/common/get-active-shopping-cart";
import GlobalNotification from "~/components/common/global-notification";
import GTMIntegration from "~/components/common/gtm-integration";
import UsableNetIntegration from "~/components/common/usable-net-integration";
import PreviewModeBar from "~/components/layout/preview-mode-bar";
import { getForterScript } from "~/components/tracking/forter-scripts";
import { AuthContextProvider } from "~/contexts/auth-context-provider";
import BreakpointProvider from "~/contexts/breakpoint";
import ConfigurationProvider from "~/contexts/configuration";
import { CountryDialogProvider } from "~/contexts/country-dialog-context";
import FavouriteStoreProvider from "~/contexts/favourite-store";
import { PageTrackingsProvider } from "~/contexts/page-trackings";
import { ServerSidePropsProvider } from "~/contexts/server-side-props";
import TextDirProvider from "~/contexts/text-dir";
import UserConsentsProvider from "~/contexts/user-consents";
import { useDatadogRumStartView } from "~/hooks/use-datadog-rum-start-view";
import { HttpService } from "~/services/http";
import Logger from "~/services/logger/logger";
import { MuiXAdapterTSLuxon } from "~/shared/mui-x-adapter-ts-luxon.class";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import intlUtils from "~/utils/intl-utils";
import { withStoragesAvailable } from "~/utils/storage-utils";

import { KIKOApplicationConfiguration } from "../../global";
import packageJson from "../../package.json";

nProgress.configure({ showSpinner: false, minimum: 0.1, speed: 300, trickleSpeed: 100 });
Router.events.on("routeChangeStart", nProgress.start);
Router.events.on("routeChangeError", nProgress.done);
Router.events.on("routeChangeComplete", nProgress.done);

type Props = {
  organizationUnitId: string;
  organizationUnitBackendId: string;
  configuration: KIKOApplicationConfiguration;
  messages?: AbstractIntlMessages;
  dehydratedState?: unknown;
  draftMode?: boolean;
  timeZones: string[];
};

setCoreSetting("appName", packageJson.name);
setCoreSetting("appVersion", packageJson.version);
setServiceSetting("backendIdMode", "StringIDs");

logger.setLevel("error");

const font = localFont({
  src: [
    {
      path: "../../public/font-thin.woff2",
      weight: "100",
    },
    {
      path: "../../public/font-extralight.woff2",
      weight: "200",
    },
    {
      path: "../../public/font-light.woff2",
      weight: "300",
    },
    {
      path: "../../public/font-regular.woff2",
      weight: "400",
    },
    {
      path: "../../public/font-semibold.woff2",
      weight: "600",
    },
    {
      path: "../../public/font-bold.woff2",
      weight: "700",
    },
  ],
});

function onError(error: IntlError) {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  const translationErrors: Record<string, boolean> = JSON.parse(sessionStorage.getItem("translationErrors") ?? "{}");

  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    // Missing translations are expected and should only log an error

    if (error.originalMessage && !translationErrors[error.originalMessage]) {
      Logger.instance.warn(error);
      translationErrors[error.originalMessage] = true;
      sessionStorage.setItem("translationErrors", JSON.stringify(translationErrors));
    }
  } else {
    // Other errors indicate a bug in the app and should be reported
    Logger.instance.error(error);
  }
}

export default function App({ Component, pageProps }: AppProps<Props>) {
  const router = useRouter();
  useDatadogRumStartView();
  HttpService.updateOrganizationUnitId(pageProps.organizationUnitId);
  const [queryClient] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          cacheTime: 10 * 60 * 1000,
          // Refetch on window focus for important data
          refetchOnWindowFocus: false, // Disabled by default, enable per-query when needed
          // Retry failed queries max 2 times with exponential backoff
          retry: 2,
          retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        },
        mutations: {
          // Retry mutations once on network errors
          retry: 1,
        },
      },
    })
  );

  const [lang, country] = router.locale?.split("-") ?? ["en", "ae"];

  // handle algolia insights init
  useEffect(() => {
    aa("init", {
      appId: process.env.NEXT_PUBLIC_ALGOLIA_APPID!,
      apiKey: process.env.NEXT_PUBLIC_ALGOLIA_APIKEY!,
    });
  }, []);

  // handle algolia query id sync when a new tab is open
  useEffect(() => {
    withStoragesAvailable((localStorage, sessionStorage) => {
      if (sessionStorage.getItem("algolia_queryid") === null) {
        const fromLocal = localStorage?.getItem("algolia_queryid");
        if (fromLocal) {
          sessionStorage.setItem("algolia_queryid", fromLocal);
          localStorage.removeItem("algolia_queryid");
        }
      }
    });
  }, [router.asPath]);

  // to debug route changes
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      Logger.instance.debug("Router: routeChangeStart to: ", url);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    // Update the current `lang` and `dir` for the html tag dynamically.
    const html = document.querySelector("html")!;

    const dir = lang === "ar" ? "rtl" : "ltr";

    html.setAttribute("lang", lang);
    html.setAttribute("dir", dir);

    CommonUtils.isWindows() && document.body.classList.add(Constants.SCROLLBAR_CLASSNAME);

    // Update the current EVA language
    if (country) {
      setCoreSetting("language", `${lang}-${country.toUpperCase()}`);
    }

    if (router.locale && router.locale !== "default") {
      sessionStorage.setItem("current-country", router.locale);
    }
  }, [country, lang, router.locale]);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/next-service-worker.js");
    }
  }, []);
  return (
    <>
      <style jsx global>{`
        :root {
          --local-font: ${font.style.fontFamily};
        }
      `}</style>
      <script
        id="viewport-height"
        dangerouslySetInnerHTML={{
          __html: `document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px' );`,
        }}
      ></script>
      <DatadogInit />
      <NextIntlClientProvider
        locale={intlUtils.getSupportedLocale(router)}
        timeZone={pageProps.timeZones?.[0]}
        messages={router.query.debugLabels ? {} : pageProps.messages}
        onError={onError}
      >
        <LocalizationProvider dateAdapter={MuiXAdapterTSLuxon} adapterLocale={router.locale}>
          <ErrorBoundary fallbackRender={() => <GenericError {...pageProps} />}>
            <BreakpointProvider>
              <TextDirProvider>
                <ConfigurationProvider
                  organizationUnitId={pageProps.organizationUnitId}
                  organizationUnitBackendId={pageProps.organizationUnitBackendId}
                  configuration={pageProps.configuration}
                  router={router}
                >
                  <ServerSidePropsProvider serverSideProps={pageProps}>
                    <QueryClientProvider client={queryClient}>
                      <AuthContextProvider>
                        <ContentfulLivePreviewProvider
                          locale={contentfulUtils.mapLocale(router.locale)}
                          enableInspectorMode={pageProps.draftMode ?? false}
                          enableLiveUpdates={pageProps.draftMode ?? false}
                          debugMode={process.env.NODE_ENV !== "production"}
                        >
                          <Hydrate state={pageProps.dehydratedState}>
                            <UserConsentsProvider>
                              <CartSync />
                              {CommonUtils.parseBoolean(pageProps.configuration?.["App:Ecommerce:EnableCartSync"]) && (
                                <GetActiveShoppingCart />
                              )}
                              <PageTrackingsProvider>
                                <FavouriteStoreProvider>
                                  <PreviewModeBar />
                                  <UsableNetIntegration />
                                  <DidomiIntegration />
                                  <GTMIntegration />
                                  <GlobalNotification />
                                  <CountryDialogProvider>
                                    {CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_COUNTRY_DETECT) && (
                                      <CountryDetectModal />
                                    )}
                                    <Component {...pageProps} />
                                  </CountryDialogProvider>
                                </FavouriteStoreProvider>
                              </PageTrackingsProvider>
                            </UserConsentsProvider>
                          </Hydrate>
                          <ReactQueryDevtools
                            initialIsOpen={false}
                            position={lang === "ar" ? "bottom-left" : "bottom-right"}
                          />
                        </ContentfulLivePreviewProvider>
                      </AuthContextProvider>
                    </QueryClientProvider>
                  </ServerSidePropsProvider>
                </ConfigurationProvider>
              </TextDirProvider>
            </BreakpointProvider>
          </ErrorBoundary>
        </LocalizationProvider>
      </NextIntlClientProvider>
      <SpeedInsights />
      {process.env.NEXT_PUBLIC_FORTER_SITE_ID && (
        <Script
          id="forter-init"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: getForterScript(process.env.NEXT_PUBLIC_FORTER_SITE_ID) }}
        />
      )}
    </>
  );
}
